import { useEffect, useCallback, ReactNode } from 'react';
import BoldTextSolid from '@simplywallst/ui-core/icons/BoldTextSolid';
import BulletListSolid from '@simplywallst/ui-core/icons/BulletListSolid';
import ImageUploadOutline from '@simplywallst/ui-core/icons/ImageUploadOutline';
import {
  Button,
  Grid,
  Box,
  ScrollPanel,
  Text,
  ToggleButton,
} from '@simplywallst/ui-core';

import { RichTextEditorInput } from './RichTextEditorInput';
import { Heading } from './components';
import { useRichTextEditor } from './useRichTextEditor';
import { StyledGridFooter, TogglesWrapper } from './styled';
import { Indicator } from './Indicator';
import { isDisabled } from './utils';
import { Editor } from '@tiptap/core';
import { Placeholder } from './components/Placeholder';

export type Content = {
  html: string;
  text: string;
};

interface Props {
  defaultContent?: string;
  placeholderText?: ReactNode;
  characterLimit?: number;
  // Neccessity as we don't allow image insertion for Notes
  // At this stage it will only be used for Narratives internally
  enableImages?: boolean;
  /**
   * todo @victorkurauchi @ricardinho
   * Code principles definiton.
   * How we want to approach our code in terms of composing them or allowing multiple props
   */
  isLoading?: boolean;
  isSuccess?: boolean;
  saveButtonText?: string;
  onSave?: (content: Content) => void;
  onUpdate?: (content: Content) => void;
  onInit?: (content: Content, editor: Editor) => void;
  onError?: (error: string) => void;
  autoFocus?: boolean;
}

export const RichTextEditor = ({
  defaultContent,
  placeholderText,
  characterLimit,
  enableImages = false,
  isLoading = false,
  isSuccess = false,
  saveButtonText,
  onSave,
  onUpdate,
  onInit,
  onError,
  autoFocus,
}: Props) => {
  const isTextualPlaceholder =
    typeof placeholderText === 'string' ||
    typeof placeholderText === 'undefined';
  const {
    editor,
    getHTML,
    getText,
    isActive,
    format,
    focus,
    getCharacterCount,
    resetEditorContent,
  } = useRichTextEditor({
    defaultContent,
    placeholderText: isTextualPlaceholder ? placeholderText : null,
    characterLimit,
    onUpdate: (editor) => {
      onUpdate?.({
        text: editor.getText(),
        html: editor.getHTML(),
      });
    },
    onInit: (editor) => {
      onInit?.(
        {
          text: editor.getText(),
          html: editor.getHTML(),
        },
        editor
      );
    },
    onError,
  });

  useEffect(() => {
    resetEditorContent();
  }, [defaultContent, resetEditorContent]);

  useEffect(() => {
    if (autoFocus) {
      focus('end');
    }
  }, [focus, autoFocus]);

  const focusEditor = useCallback(() => {
    if (getCharacterCount() === 0) {
      focus('end');
    }
  }, [getCharacterCount, focus]);

  return (
    <Grid templateRows="auto max-content" gap="8px">
      {editor && (
        <Box
          data-cy-id="rte-wrapper"
          position="relative"
          minHeight={200}
          onClick={focusEditor}
        >
          <ScrollPanel
            position="absolute"
            overflow="hidden auto"
            height="100%"
            width="100%"
          >
            <RichTextEditorInput editor={editor} />
            {!isTextualPlaceholder && placeholderText && (
              <Placeholder editor={editor} placeholder={placeholderText} />
            )}
          </ScrollPanel>
        </Box>
      )}
      <StyledGridFooter
        as="footer"
        templateColumns="max-content max-content"
        justifyContent="space-between"
      >
        <TogglesWrapper gap="4px" alignItems="center">
          <Heading format={format} isActive={isActive} />
          <ToggleButton
            styleType="subtle"
            onClick={() => format('bold')}
            disabled={isDisabled(
              ['heading1', 'heading2', 'heading3'],
              isActive
            )}
            state={isActive('bold') ? 'on' : 'off'}
          >
            <BoldTextSolid />
          </ToggleButton>
          <ToggleButton
            styleType="subtle"
            onClick={() => format('bulletList')}
            disabled={isDisabled(
              ['heading1', 'heading2', 'heading3'],
              isActive
            )}
            state={isActive('bulletList') ? 'on' : 'off'}
          >
            <BulletListSolid />
          </ToggleButton>
          {enableImages && (
            <ToggleButton
              styleType="subtle"
              onClick={() => format('image')}
              disabled={isDisabled(
                [
                  'heading1',
                  'heading2',
                  'heading3',
                  'bold',
                  'bulletList',
                  'orderedList',
                  'link',
                ],
                isActive
              )}
              state={isActive('image') ? 'on' : 'off'}
            >
              <ImageUploadOutline />
            </ToggleButton>
          )}
        </TogglesWrapper>
        <Grid
          templateColumns="repeat(3, max-content)"
          gap="16px"
          alignItems="center"
        >
          <Grid>
            {isLoading && (
              <Text typography="tiny" color="mediumGray">
                Saving...
              </Text>
            )}
            {isSuccess && (
              <Text typography="tiny" color="mediumGray">
                Saved
              </Text>
            )}
          </Grid>
          {characterLimit && (
            <Grid templateColumns="24px">
              <Indicator
                percent={getCharacterCount() / characterLimit}
                percentWarnLimit={0.9}
              />
            </Grid>
          )}
          {onSave && (
            <Button
              styleType="loudLarge"
              disabled={isLoading || getText().length === 0}
              onClick={() =>
                onSave?.({
                  text: getText(),
                  html: getHTML(),
                })
              }
            >
              {saveButtonText || 'Save'}
            </Button>
          )}
        </Grid>
      </StyledGridFooter>
    </Grid>
  );
};
